import React, { useState, useEffect } from 'react';
import { Grid } from '@material-ui/core';
import { map } from 'lodash';
import PropTypes from 'prop-types';

import {
  RadioGroupSection,
  FormActionButtons,
  ErrorContainer,
  SearchTextListContainer,
} from '../../../../components';
import {
  ID_PUBLIC,
  ID_PRIVATE,
  ID_RESTRICTED,
  MANAGEMENT_PERMISSIONS_DATA,
  MANAGEMENT_USER_TITLE,
  MANAGEMENT_USER_SUB_TITLE,
  MANAGEMENT_USER_PLACE_HOLDER_TEXT,
  MANAGEMENT_SELECTED_USER_TITLE,
  MANAGEMENT_SELECTED_USER_SUB_TITLE,
  MANAGEMENT_SELECTED_USER_PLACE_HOLDER_TEXT,
  MANAGEMENT_SELECTED_ITEM_ERROR_MESSAGE,
  SAVE_AND_NEXT_TEXT,
} from '../labels';
import { getSingularRoleName, hasPublisherRole, getApiOwnerUser } from '../../../../utils';

const ADMIN_PERMISSIONS_DATA = MANAGEMENT_PERMISSIONS_DATA.filter(option =>
  option.id !== ID_PRIVATE);


export default function ManagementPermission(props) {
  const {
    classes,
    userContext,
    apiUuid,
    tabValue,
    users,
    onCancel,
    errors,
   } = props;

  const isDisabled = !apiUuid;
  const [isChangePermission, setIsChangePermission] = useState(false);
  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);
  const [selectedPermission, setPermission] = useState('');

  const getDefaultPermission = () => {
    let defaultPermission = selectedPermission;
    if (!isChangePermission) {
      if (userContext.portalAdmin) {
        defaultPermission = props.defaultSelectedUsers.length > 0 ? ID_RESTRICTED :
        ID_PUBLIC;
      } else if (userContext.apiOwner) {
        if (props.defaultSelectedUsers.length === 0) {
          defaultPermission = ID_PUBLIC;
        } else if (props.defaultSelectedUsers.length === 1) {
          defaultPermission = ID_PRIVATE;
        } else {
          defaultPermission = ID_RESTRICTED;
        }
      }
    }
    return defaultPermission;
  };
  const [selectedUsers, setSelectedUsers] = useState(props.selectedUsers);
  const data = userContext.portalAdmin ?
    ADMIN_PERMISSIONS_DATA : MANAGEMENT_PERMISSIONS_DATA;

  const fetchManagementData = async () => {
    await props.fetchApi(apiUuid);
    await props.fetchSelectedUsers(apiUuid);
    await props.fetchAvailableUsers(apiUuid);
  };

  useEffect(() => {
    if ((hasPublisherRole(userContext)) && apiUuid
      && tabValue === 'management-tab') {
      fetchManagementData();
    }
  }, [tabValue]);

  useEffect(() => {
    setPermission(getDefaultPermission());
  }, [props.selectedUsers]);

  useEffect(() => {
    setSelectedUsers(props.selectedUsers);
  }, [props.selectedUsers]);

  const onSearchListChange = (selectedUser) => {
    setSelectedUsers([...selectedUser]);
    setIsUnsavedChanges(true);
    localStorage.setItem('isUnSavedChanges', true);
  };

  const onChangePermission = (e) => {
    setPermission(e.target.value);
    setIsChangePermission(true);
    setIsUnsavedChanges(true);
    localStorage.setItem('isUnSavedChanges', true);
  };

  const onNextManagement = async (permission, manageUsers) => {
    let dataToSave = [];

     // only api owner can choose private option
     // If an API Owner chooses the 'private' option -
     // add the API Owner UUID to the selected users able to access the API
    if (permission === ID_PRIVATE) {
      const apiOwner = getApiOwnerUser(userContext);
      dataToSave = [{ uuid: apiOwner.uuid }];
    }
    if (permission === ID_RESTRICTED) {
      map(manageUsers, object => {
        dataToSave.push({ uuid: object.uuid });
      });
    }
    await props.savePermissions(dataToSave, apiUuid);
  };

  const onNext = () => {
    if (selectedPermission === ID_RESTRICTED && selectedUsers.length === 0) {
      return false;
    }
    if (selectedPermission === ID_PUBLIC) {
      setSelectedUsers([]);
    }
    setIsUnsavedChanges(false);
    localStorage.setItem('isUnSavedChanges', false);
    onNextManagement(selectedPermission, selectedUsers);
    return true;
  };

  const onClickNext = () => {
    onNext();
  };

  const getItemSecondaryText = (user) => user.roleOrgs.map(role => getSingularRoleName(role));
  return (
    <div id="api-management-container">
      <h1 className={classes.pageTitle}>Management Permissions</h1>
      <h2 className={classes.sectionTitle} id="manage-user-container">
        API Owner Permissions
      </h2>
      <RadioGroupSection
        selected={selectedPermission}
        onChange={onChangePermission}
        data={data}
        id="management-radio-group"
      />
      {errors.length > 0 &&
        <ErrorContainer errors={errors} />
      }
      {selectedPermission === ID_RESTRICTED &&
        <Grid container spacing={3} className={classes.gridContainer}>
          <Grid md={6} item>
            <SearchTextListContainer
              title={MANAGEMENT_USER_TITLE}
              subtitle={MANAGEMENT_USER_SUB_TITLE}
              placeHolderText={MANAGEMENT_USER_PLACE_HOLDER_TEXT}
              defaultList={users}
              defaultSelectedList={selectedUsers}
              listId={'users-list'}
              isCheckBoxVisible
              onSearchListChange={onSearchListChange}
              isDisableSelectedOption
              disableSelectedOptions={[userContext]}
              userContext={userContext}
              getItemSecondaryText={getItemSecondaryText}
            />
          </Grid>
          <Grid md={6} item>
            <SearchTextListContainer
              title={MANAGEMENT_SELECTED_USER_TITLE}
              subtitle={MANAGEMENT_SELECTED_USER_SUB_TITLE}
              placeHolderText={MANAGEMENT_SELECTED_USER_PLACE_HOLDER_TEXT}
              isSelectedNumberVisible
              defaultList={selectedUsers}
              defaultSelectedList={selectedUsers}
              listId={'users-selected-list'}
              onSearchListChange={onSearchListChange}
              isDeleteIconVisible
              isDisableSelectedOption
              userContext={userContext}
              disableSelectedOptions={[userContext]}
              getItemSecondaryText={getItemSecondaryText}
              selectedItemErrorMessage={MANAGEMENT_SELECTED_ITEM_ERROR_MESSAGE}
            />
          </Grid>
        </Grid>
      }
      <FormActionButtons
        nextText={SAVE_AND_NEXT_TEXT}
        onNextClick={onClickNext}
        onCancelClick={() => onCancel(isUnsavedChanges)}
        id="management-form-buttons"
        isDisabled={isDisabled}
      />
    </div>
  );
}

ManagementPermission.propTypes = {
  classes: PropTypes.object,
  userContext: PropTypes.object,
  selectedUsers: PropTypes.arrayOf(PropTypes.object),
  users: PropTypes.arrayOf(PropTypes.object),
  onCancel: PropTypes.func,
  errors: PropTypes.arrayOf(PropTypes.object),
  apiUuid: PropTypes.string,
  tabValue: PropTypes.string,
  apiDetails: PropTypes.object,
  defaultSelectedUsers: PropTypes.object,
  fetchApi: PropTypes.func,
  fetchSelectedUsers: PropTypes.func,
  fetchAvailableUsers: PropTypes.func,
  savePermissions: PropTypes.func,
};
